/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';
import type { MakeBridgeContext } from '../../types';
import { createBridgeResource } from '../../bridge-resource';
import type { TBridgeMethod } from '../types';
import { BridgeClientError } from '../../../custom-ui/bridge';
import type { IEnvironment } from 'relay-runtime';
import type { PermissionMethod } from '../../../custom-ui/bridge/methods/permissions/usePermission';
import type {
	EgressGetPayload,
	EgressSetPayload,
	EgressDeleteDomainPayload,
	EgressDeleteGroupPayload,
	RemoteGetPayload,
	RemoteSetPayload,
} from '../../../custom-ui/bridge/methods/permissions/types';
import { AdminConsentModal } from './AdminConsentModal';
import type { ManifestRemoteMap } from './AdminConsentModal';
import { getForgeApp } from './getForgeApp';
import { egressSet as egressSetWithQueue, remoteSet as remoteSetWithQueue } from './indexWithQueue';

// ─── Rate-limit resource ──────────────────────────────────────────────────────

const RATE_LIMIT_MS = 5000;

type PermissionModalState = {
	isModalOpen: boolean;
	isRateLimited: boolean;
};

/**
 * Per-instance bridge resource that tracks the admin consent modal's open state
 * and whether the rate limit is in effect. Using a bridge resource rather than
 * React state keeps the modal logic stateless from the bridge method's perspective
 * while still being safely scoped to each iframe instance.
 */
const permissionModalResource = createBridgeResource<PermissionModalState>(
	'permissionModal',
	() => ({ isRateLimited: false, isModalOpen: false }),
	() => {},
);

// ─── Context type ─────────────────────────────────────────────────────────────

type PermissionContext = MakeBridgeContext<
	'claimBridgeResource' | 'contextIds' | 'extension' | 'extensionDetails',
	'analytics' | 'overlayController' | 'permissionFetchClient' | 'relayEnvironment'
>;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fetchAppData = async (
	relayEnvironment: IEnvironment,
	appId: string,
	contextIds: string[],
) => {
	try {
		return await getForgeApp(relayEnvironment, appId, contextIds);
	} catch (e) {
		if (e instanceof Error) {
			const error = new BridgeClientError(e.message);
			error.stack = e.stack;
			throw error;
		}
		throw new BridgeClientError(typeof e === 'string' ? e : String(e));
	}
};

const trackPermission = (
	analytics: PermissionContext['services']['analytics'],
	appId: string,
	installationId: string,
	action: PermissionMethod,
) => {
	analytics.tracker.trackPermissionAction({ appId, installationId, action });
};

const canOpenModal = (state: PermissionModalState): boolean =>
	!state.isModalOpen && !state.isRateLimited;

const noteModalOpened = (state: PermissionModalState): void => {
	state.isModalOpen = true;
	state.isRateLimited = true;
	setTimeout(() => {
		state.isRateLimited = false;
	}, RATE_LIMIT_MS);
};

const noteModalClosed = (state: PermissionModalState): void => {
	state.isModalOpen = false;
};

// ─── egressGet ────────────────────────────────────────────────────────────────

/**
 * Returns the current egress allowlist for the app installation.
 * Delegates to the permission REST API and returns the parsed JSON response.
 */
export const egressGet: TBridgeMethod<PermissionContext, EgressGetPayload, unknown> = async (
	{
		services: { permissionFetchClient, analytics },
		extensionDetails: { appId },
		extension: { installationId },
	},
	payload,
) => {
	if (!fg('configurable-egress-mvp-frontend')) {
		return {};
	}

	trackPermission(analytics, appId, installationId, 'egressGet');

	try {
		const resp = await permissionFetchClient.egressGet(payload);
		const json = await resp.json();
		if (!resp.ok) {
			throw new BridgeClientError(JSON.stringify(json, null, 2));
		}
		return json;
	} catch (err) {
		throw new BridgeClientError(`Could not get egress due to: ${err}`);
	}
};

// ─── egressSet ────────────────────────────────────────────────────────────────

/**
 * Updates the egress allowlist for the app installation.
 *
 * Performs a dry run first to validate the payload. If the dry run succeeds,
 * the admin consent modal is always shown via `overlayController.show`. The
 * method resolves once the admin approves and the real PUT is applied, or
 * rejects when the admin cancels.
 */
export const egressSet: TBridgeMethod<PermissionContext, EgressSetPayload, unknown> = async (
	{
		services: { permissionFetchClient, analytics, overlayController, relayEnvironment },
		extensionDetails: { appId },
		extension: { installationId },
		contextIds,
		claimBridgeResource,
	},
	payload,
) => {
	if (!fg('configurable-egress-mvp-frontend')) {
		return {};
	}

	trackPermission(analytics, appId, installationId, 'egressSet');

	const app = await fetchAppData(relayEnvironment, appId, contextIds);

	const dryRunResp = await permissionFetchClient.egressSet(payload, true);
	if (!dryRunResp.ok) {
		throw new BridgeClientError('Dry run failed');
	}

	const modalState = claimBridgeResource(permissionModalResource);
	if (!canOpenModal(modalState)) {
		throw new BridgeClientError(
			'Permission request rate limited. Please wait before making another request.',
		);
	}

	return new Promise((resolve, reject) => {
		noteModalOpened(modalState);
		overlayController.show(
			<AdminConsentModal
				appName={app.name}
				domainInfo={{ type: 'EGRESS', payload }}
				termsOfUseLink={app.termsOfService ?? null}
				privacyPolicyLink={app.privacyPolicy ?? null}
				onCancel={() => {
					noteModalClosed(modalState);
					reject(new BridgeClientError('Setting egress was rejected by admin'));
				}}
				onConfirm={async () => {
					const setResp = await permissionFetchClient.egressSet(payload, false);
					const json = await setResp.json();
					if (!setResp.ok) {
						throw new BridgeClientError(JSON.stringify(json, null, 2));
					}
					noteModalClosed(modalState);
					resolve(json);
				}}
			/>,
			false,
		);
	});
};

// ─── egressDeleteDomain ───────────────────────────────────────────────────────

/**
 * Removes a single domain from the egress allowlist.
 */
export const egressDeleteDomain: TBridgeMethod<
	PermissionContext,
	EgressDeleteDomainPayload,
	unknown
> = async (
	{
		services: { permissionFetchClient, analytics },
		extensionDetails: { appId },
		extension: { installationId },
	},
	payload,
) => {
	if (!fg('configurable-egress-mvp-frontend')) {
		return {};
	}

	trackPermission(analytics, appId, installationId, 'egressDeleteDomain');

	try {
		const resp = await permissionFetchClient.egressDeleteDomain(payload);
		if (resp.ok && resp.status === 204) {
			return undefined;
		}
		const json = await resp.json();
		if (!resp.ok) {
			throw new BridgeClientError(JSON.stringify(json, null, 2));
		}
		return json;
	} catch (err) {
		throw new BridgeClientError(`Could not delete egress domain due to ${err}`);
	}
};

// ─── egressDeleteGroup ────────────────────────────────────────────────────────

/**
 * Removes an entire egress group (all domains under a key) from the allowlist.
 */
export const egressDeleteGroup: TBridgeMethod<
	PermissionContext,
	EgressDeleteGroupPayload,
	unknown
> = async (
	{
		services: { permissionFetchClient, analytics },
		extensionDetails: { appId },
		extension: { installationId },
	},
	payload,
) => {
	if (!fg('configurable-egress-mvp-frontend')) {
		return {};
	}

	trackPermission(analytics, appId, installationId, 'egressDeleteGroup');

	try {
		const resp = await permissionFetchClient.egressDeleteGroup(payload);
		if (resp.ok && resp.status === 204) {
			return undefined;
		}
		const json = await resp.json();
		if (!resp.ok) {
			throw new BridgeClientError(JSON.stringify(json, null, 2));
		}
		return json;
	} catch (err) {
		throw new BridgeClientError(`Could not delete egress group due to ${err}`);
	}
};

// ─── remoteGet ────────────────────────────────────────────────────────────────

/**
 * Returns the current remote endpoint configuration for the app installation.
 */
export const remoteGet: TBridgeMethod<PermissionContext, RemoteGetPayload, unknown> = async (
	{
		services: { permissionFetchClient, analytics },
		extensionDetails: { appId },
		extension: { installationId },
	},
	payload,
) => {
	if (!fg('configurable-egress-mvp-frontend')) {
		return {};
	}

	trackPermission(analytics, appId, installationId, 'remoteGet');

	try {
		const resp = await permissionFetchClient.remoteGet(payload);
		const json = await resp.json();
		if (!resp.ok) {
			throw new BridgeClientError(JSON.stringify(json, null, 2));
		}
		return json;
	} catch (err) {
		throw new BridgeClientError(`Could not get remote due to: ${err}`);
	}
};

// ─── remoteSet ────────────────────────────────────────────────────────────────

/**
 * Updates the remote endpoint configuration for the app installation.
 *
 * Always shows the admin consent modal to allow the admin to verify the
 * configured endpoint before applying it. The dry run verifies the payload
 * is structurally valid before opening the modal.
 */
export const remoteSet: TBridgeMethod<PermissionContext, RemoteSetPayload, unknown> = async (
	{
		services: { permissionFetchClient, analytics, overlayController, relayEnvironment },
		extensionDetails: { appId },
		extension: { installationId },
		contextIds,
		claimBridgeResource,
	},
	payload,
) => {
	if (!fg('configurable-egress-mvp-frontend')) {
		return {};
	}

	trackPermission(analytics, appId, installationId, 'remoteSet');

	const app = await fetchAppData(relayEnvironment, appId, contextIds);

	const dryRunResp = await permissionFetchClient.remoteSet(payload, true);
	if (!dryRunResp.ok) {
		throw new BridgeClientError('Dry run failed');
	}

	const modalState = claimBridgeResource(permissionModalResource);
	if (!canOpenModal(modalState)) {
		throw new BridgeClientError(
			'Permission request rate limited. Please wait before making another request.',
		);
	}

	// Build the manifest remote map from the app's extension nodes so the modal
	// can show descriptive names and supported patterns for each remote key.
	const node = (app.installationsByContexts?.edges ?? [])[0]?.node;
	const payloadKeys = new Set(payload.remotes.map((r) => r.key));
	const manifestRemoteMap = (node?.appEnvironmentVersion?.extensions?.nodes ?? [])
		.filter(
			(e) => e && e.extensionData.type === 'core:remote' && payloadKeys.has(e.extensionData.key),
		)
		.reduce((acc, e) => {
			if (e?.extensionData) {
				acc[e.extensionData.key] = e.extensionData.configurable;
			}
			return acc;
		}, {} as ManifestRemoteMap);

	return new Promise((resolve, reject) => {
		noteModalOpened(modalState);
		overlayController.show(
			<AdminConsentModal
				appName={app.name}
				domainInfo={{ type: 'REMOTE', payload, manifestRemoteMap }}
				termsOfUseLink={app.termsOfService ?? null}
				privacyPolicyLink={app.privacyPolicy ?? null}
				onCancel={() => {
					noteModalClosed(modalState);

					reject(new BridgeClientError('Setting remote was rejected by admin'));
				}}
				onConfirm={async () => {
					const setResp = await permissionFetchClient.remoteSet(payload, false);
					if (!setResp.ok) {
						throw new BridgeClientError(setResp.statusText);
					}
					noteModalClosed(modalState);
					resolve(undefined);
				}}
			/>,
			false,
		);
	});
};

// ─── Exported map ─────────────────────────────────────────────────────────────

export const permissionBridgeMethods: {
	__permission__egressDeleteDomain: TBridgeMethod<
		PermissionContext,
		EgressDeleteDomainPayload,
		unknown
	>;
	__permission__egressDeleteGroup: TBridgeMethod<
		PermissionContext,
		EgressDeleteGroupPayload,
		unknown
	>;
	__permission__egressGet: TBridgeMethod<PermissionContext, EgressGetPayload, unknown>;
	__permission__egressSet: TBridgeMethod<PermissionContext, EgressSetPayload, unknown>;
	__permission__remoteGet: TBridgeMethod<PermissionContext, RemoteGetPayload, unknown>;
	__permission__remoteSet: TBridgeMethod<PermissionContext, RemoteSetPayload, unknown>;
} = {
	__permission__egressGet: egressGet,
	__permission__egressSet: (ctx, payload) => {
		if (fg('forge-ui-permission-consent-modal-improvements')) {
			return egressSetWithQueue(ctx, payload);
		}
		return egressSet(ctx, payload);
	},
	__permission__egressDeleteDomain: egressDeleteDomain,
	__permission__egressDeleteGroup: egressDeleteGroup,
	__permission__remoteGet: remoteGet,
	__permission__remoteSet: (ctx, payload) => {
		if (fg('forge-ui-permission-consent-modal-improvements')) {
			return remoteSetWithQueue(ctx, payload);
		}
		return remoteSet(ctx, payload);
	},
};

export type PermissionBridgeMethods = typeof permissionBridgeMethods;
